<script lang="ts">
  import { app, persistSettings, applyProject, startVaseEdit, commitVaseEdit, cancelVaseEdit, vaseEditUndo, penEditUndo, type CameraView, type AppMode, type RightPanel } from './store.svelte';
  import PenCanvas from './PenCanvas.svelte';
  import ParamEditor from './ParamEditor.svelte';
  import ChatPanel from './ChatPanel.svelte';
  import { chatStore, setChatOpen } from './llm/chatStore.svelte';
  import { installMcpGateway } from './llm/mcp';
  import { evalGraph, sitOnPlate } from '../core/paramGraph';
  import { NODE_REGISTRY } from '../core/paramNodes';
  import { applyPatternDisplacement } from '../core/cpuDisplace';
  import { normalizeAndSerialize } from './patternSerialize';
  import { svgToTextureGrid } from './patterns';
  import { DEFAULT_VASE } from '../core/vaseModel';
  import Icon from './lib/Icon.svelte';
  import IconButton from './lib/IconButton.svelte';
  import MagicButton from './lib/MagicButton.svelte';
  import { t, type Lang } from './i18n';
  import { parseStl, centerOnPlate } from '../core/mesh';
  import { sliceInWorker } from '../worker/slicerClient';
  import { downloadProject, loadProjectFromFile } from './projectIO';
  import { exportBinaryStl, exportAsciiStl, exportObj, exportThreeMf } from '../core/stlExport';
  import Header from './Header.svelte';
  import LeftSidebar from './LeftSidebar.svelte';
  import ErrorBoundary from './ErrorBoundary.svelte';
  import InsertPanel from './InsertPanel.svelte';
  import VasePanel from './VasePanel.svelte';
  import TemplatesPanel from './TemplatesPanel.svelte';
  import SvgGeneratorModal from './SvgGeneratorModal.svelte';
  import SculptPanel from './SculptPanel.svelte';
  import { onMount } from 'svelte';

  // Lazy-load Viewer (and via it, three.js) — biggest single chunk in the bundle.
  // Splitting it lets the UI shell paint before WebGL boots.
  let ViewerComp: typeof import('./Viewer.svelte').default | null = $state(null);
  onMount(async () => {
    const mod = await import('./Viewer.svelte');
    ViewerComp = mod.default;
  });

  // Suppress unused import lint — Lang is re-exported transitively via Header
  void persistSettings;
  type _UnusedLang = Lang;

  let isDragging = $state(false);
  let showInsert = $state(false);
  let projectInput: HTMLInputElement | undefined = $state();
  /** Modal: "Stüdyo'ya geçiş — mevcut modelle devam mı, sıfırdan mı?" */
  let studioPrompt = $state(false);
  /** AI chat panel toggle — backed by chatStore so it survives mode switches
   *  and is shared across any other component that needs it. */
  let chatOpen = $derived(chatStore.open);
  function toggleChat() { setChatOpen(!chatStore.open); }

  // MCP postMessage gateway — install once on mount so external hosts (Claude
  // Code wrapper, MCP server bridge) can drive the parametric graph.
  onMount(() => { installMcpGateway(); });

  // Always-on graph re-eval. ParamEditor's own $effect handles this only
  // while the user is in Parametric mode; here we cover every other mode
  // (edit/sculpt/preview/slice/studio) so chat-driven graph mutations
  // immediately update the displayed mesh — without forcing the user to
  // round-trip through param mode to "wake up" the eval.
  let evalScheduled = false;
  $effect(() => {
    void app.paramGraph.nodes.length;
    void app.paramGraph.wires.length;
    for (const n of app.paramGraph.nodes) {
      void n.x; void n.y; void n.type;
      for (const k of Object.keys(n.params)) void n.params[k];
    }
    void app.paramPattern;     // pattern set/clear also forces a re-eval
    if (app.mode === 'param') return;   // ParamEditor owns eval in its mode
    if (app.paramGraph.nodes.length === 0) return;
    if (evalScheduled) return;
    evalScheduled = true;
    requestAnimationFrame(() => {
      evalScheduled = false;
      const r = evalGraph(app.paramGraph, NODE_REGISTRY);
      const seated = sitOnPlate(r.triangles);
      app.triangles = app.paramPattern
        ? applyPatternDisplacement(seated, app.paramPattern)
        : seated;
    });
  });

  function saveProject() {
    window.dispatchEvent(new CustomEvent('clayforge:sculpt-commit'));
    (window as unknown as { __clayforgeEnsureVaseBaked?: () => void }).__clayforgeEnsureVaseBaked?.();
    downloadProject(app);
  }
  async function loadProjectClick() { projectInput?.click(); }
  async function onProjectFile(e: Event) {
    const f = (e.target as HTMLInputElement).files?.[0];
    if (!f) return;
    try {
      const data = await loadProjectFromFile(f);
      applyProject({
        triangles: data.triangles,
        vaseParams: data.vaseParams as Partial<typeof app.vaseParams>,
        brush: data.brush,
        settings: data.settings,
        printerId: data.printerId,
        textureResolution: data.textureResolution,
        cameraView: data.cameraView,
        lang: data.lang,
        theme: data.theme,
        fileName: data.fileName,
        paramGraph: data.paramGraph
      });
    } catch (err) {
      app.errorMessage = (err as Error).message || t(app.lang, 'project.invalid');
    } finally {
      (e.target as HTMLInputElement).value = '';
    }
  }

  async function handleDroppedFile(file: File) {
    if (!file) return;
    if (file.name.toLowerCase().endsWith('.stl')) {
      app.status = 'loading';
      app.errorMessage = '';
      app.fileName = file.name;
      try {
        const buf = await file.arrayBuffer();
        const tris = parseStl(buf);
        if (tris.length === 0) { app.errorMessage = 'Empty STL'; app.status = 'idle'; return; }
        app.triangles = centerOnPlate(tris);
        app.sliceResult = null;
        app.gcodeOutput = null;
        app.viewMode = 'mesh';
        app.showVasePanel = false; // dropped STL is a non-vase mesh
        app.status = 'ready';
      } catch (err) {
        app.errorMessage = (err as Error).message;
        app.status = 'idle';
      }
    }
  }

  function onDrop(e: DragEvent) {
    e.preventDefault();
    isDragging = false;
    const f = e.dataTransfer?.files?.[0];
    if (f) handleDroppedFile(f);
  }
  function onDragOver(e: DragEvent) { e.preventDefault(); isDragging = true; }
  function onDragLeave() { isDragging = false; }

  async function doSlice() {
    // Commit any active sculpt edits AND lazily bake any GPU-only vase geometry
    // into app.triangles before the slicer snapshots them.
    window.dispatchEvent(new CustomEvent('clayforge:sculpt-commit'));
    (window as unknown as { __clayforgeEnsureVaseBaked?: () => void }).__clayforgeEnsureVaseBaked?.();
    if (app.triangles.length === 0) return;
    app.status = 'slicing';
    app.errorMessage = '';
    try {
      const job = $state.snapshot({
        triangles: app.triangles,
        settings: app.settings,
        printer: app.printer
      });
      const { sliceResult, gcodeOutput } = await sliceInWorker(job);
      app.sliceResult = sliceResult;
      app.currentLayerIndex = sliceResult.layers.length - 1;
      app.gcodeOutput = gcodeOutput;
      app.viewMode = 'layers';
      app.mode = 'preview';
      app.rightPanel = 'gcode';
      app.status = 'ready';
    } catch (err) {
      app.errorMessage = (err as Error).message;
      app.status = 'ready';
    }
  }

  function downloadBlob(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  }
  function baseName(): string {
    return (app.fileName || 'model').replace(/\.(stl|obj|3mf|gcode|gen|json)$/i, '');
  }
  function exportMesh(format: 'stl' | 'stl-ascii' | 'obj' | '3mf') {
    window.dispatchEvent(new CustomEvent('clayforge:sculpt-commit'));
    (window as unknown as { __clayforgeEnsureVaseBaked?: () => void }).__clayforgeEnsureVaseBaked?.();
    if (app.triangles.length === 0) return;
    const tris = app.triangles;
    const name = baseName();
    if (format === 'stl') downloadBlob(exportBinaryStl(tris), `${name}.stl`);
    else if (format === 'stl-ascii') downloadBlob(exportAsciiStl(tris, name), `${name}.ascii.stl`);
    else if (format === 'obj') downloadBlob(exportObj(tris, name), `${name}.obj`);
    else if (format === '3mf') downloadBlob(exportThreeMf(tris, name), `${name}.3mf`);
  }

  function downloadGcode() {
    if (!app.gcodeOutput) return;
    const blob = new Blob([app.gcodeOutput.gcode], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = app.fileName.replace(/\.(stl|gen|obj)$/i, '') + '.gcode';
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  }
  async function copyGcode() {
    if (!app.gcodeOutput) return;
    await navigator.clipboard.writeText(app.gcodeOutput.gcode);
  }

  $effect(() => { document.documentElement.dataset.theme = app.theme; });

  // Global Ctrl+Z while editing the vase profile triggers undo.
  $effect(() => {
    const onKey = (e: KeyboardEvent) => {
      const inPen = (app.editTool === 'pen' || app.editTool === 'crossSection' || app.editTool === 'pattern') && app.showVasePanel && app.mode === 'edit';
      if (!app.vaseEditingProfile && !inPen) return;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z' && !e.shiftKey) {
        e.preventDefault();
        if (inPen) penEditUndo();
        else vaseEditUndo();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });
  // Persist any state mutation to localStorage. Reading these in $effect
  // makes Svelte 5 track them — any change triggers persistSettings.
  $effect(() => {
    void app.vaseParams; void app.brush; void app.textureResolution;
    void app.cameraView; void app.lang; void app.theme; void app.printer.id;
    void app.settings;
    persistSettings();
  });

  function gcodePreview(): string {
    if (!app.gcodeOutput) return '';
    const lines = app.gcodeOutput.gcode.split('\n');
    if (lines.length <= 200) return app.gcodeOutput.gcode;
    return [...lines.slice(0, 100), `; …${lines.length - 200} lines elided…`, ...lines.slice(-100)].join('\n');
  }

  const cameraOptions: { id: CameraView; label: string }[] = [
    { id: 'iso', label: 'Iso' },
    { id: 'persp', label: 'Persp' },
    { id: 'top', label: 'Top' },
    { id: 'front', label: 'Front' },
    { id: 'side', label: 'Side' }
  ];
  const displayOptions: { id: 'default' | 'shaded' | 'textured' | 'wireframe'; label: string; title: string }[] = [
    { id: 'default', label: 'Default', title: 'Düz aydınlatma' },
    { id: 'shaded', label: 'Shaded', title: 'Gölgeli + rim ışık' },
    { id: 'textured', label: 'Textured', title: 'Texture renk olarak' },
    { id: 'wireframe', label: 'Wire', title: 'Sadece edge çizgileri' }
  ];

  function setMode(m: AppMode) {
    // Commit any active sculpt synchronously so downstream consumers see fresh triangles.
    window.dispatchEvent(new CustomEvent('clayforge:sculpt-commit'));
    // Auto-commit any active vase profile edit when leaving Edit mode — otherwise
    // the edit state lingers (vaseEditingProfile=true) and the overlay UI gets stuck
    // showing commit/cancel pills with broken backing state.
    if (app.vaseEditingProfile && m !== 'edit') {
      commitVaseEdit();
    }
    // Lazy-bake GPU vase to triangles before any consumer (slicer/sculpt) reads them.
    (window as unknown as { __clayforgeEnsureVaseBaked?: () => void }).__clayforgeEnsureVaseBaked?.();
    // Studio entry: if there's existing content (vase params or mesh) ask the
    // user whether to keep it or start fresh. Otherwise jump straight in.
    if (m === 'studio' && app.mode !== 'studio') {
      const hasContent = app.triangles.length > 0 || (app.showVasePanel && app.vaseParams.penProfile);
      if (hasContent) {
        studioPrompt = true;
        return;
      }
    }
    enterMode(m);
  }

  function enterMode(m: AppMode) {
    // Defer the heavy state flip behind rAF so the click feedback paints first
    // (the GPU mesh tear-down + rebuild on mode-flip can otherwise stall the UI
    // for a beat — the user perceives that as a frozen click).
    requestAnimationFrame(() => doEnterMode(m));
  }

  function doEnterMode(m: AppMode) {
    // When leaving param mode for any mesh-consuming mode, force a synchronous
    // graph eval so app.triangles is guaranteed populated before the Viewer
    // (which lazy-mounts on first non-param entry) reads them. ParamEditor's
    // $effect drove eval while it was mounted, but its last run may be stale
    // (e.g. AI dispatched a clear_graph + add_node sequence and only the
    // intermediate empty state was committed). The global App.svelte $effect
    // would normally catch this on the next reactive flush, but it schedules
    // via RAF — so the Viewer's first rebuildMesh on mount can race ahead of
    // the eval and render an empty scene. Eval-on-transition closes that gap.
    if (app.mode === 'param' && m !== 'param' && app.paramGraph.nodes.length > 0) {
      const r = evalGraph(app.paramGraph, NODE_REGISTRY);
      const seated = sitOnPlate(r.triangles);
      app.triangles = app.paramPattern
        ? applyPatternDisplacement(seated, app.paramPattern)
        : seated;
      // Defer the handoff one frame so Viewer's onMount (which is what makes
      // the listener live) runs after the {#if app.mode === 'param'} block
      // unmounts ParamEditor and mounts ViewerComp. Without the rAF, the
      // event fires while no listener is attached and the camera never reframes.
      requestAnimationFrame(() => {
        window.dispatchEvent(new CustomEvent('clayforge:param-mesh-handoff'));
      });
    }
    app.mode = m;
    if (m === 'slice' && app.triangles.length > 0 && !app.sliceResult) doSlice();
    else if (m === 'preview' && app.sliceResult) app.viewMode = 'layers';
    else if (m === 'sculpt') { app.viewMode = 'mesh'; app.rightPanel = 'sculpt'; }
    else if (m === 'edit') {
      app.viewMode = 'mesh';
      // Restore the legacy handles-based flow when entering Edit mode so the
      // "Profil Düzenle" pill is reachable. Pen/Kesit/Pattern remain available
      // via display-overlay chips for users who prefer the 2D workflow.
      if (app.editTool !== 'handles') app.editTool = 'handles';
    }
    else if (m === 'studio') {
      // Stüdyo: empty canvas + Pen tool ready immediately. Auto-open vase panel
      // (right side) when target is vase so live params are visible.
      app.viewMode = 'mesh';
      if (app.studioTarget === 'vase') {
        app.showVasePanel = true;
        app.rightPanel = 'vase';
      } else {
        app.rightPanel = 'model';
      }
      // Default to pen tool for immediate drawing.
      if (app.editTool !== 'pen' && app.editTool !== 'crossSection' && app.editTool !== 'pattern') {
        app.editTool = 'pen';
      }
    }
    else if (m === 'param') {
      // Parametrik: ParamEditor takes over the viewer area. Output node feeds
      // app.triangles so slice/preview pipelines just work after switching modes.
      app.viewMode = 'mesh';
      app.rightPanel = 'model';
      app.showVasePanel = false;
      app.vaseEditingProfile = false;
    }
  }

  /** True when the Pen-canvas pane should be visible. Studio mode always shows
   *  it (it's the whole point); edit mode shows it only when a Pen tool is active. */
  let penPaneOpen = $derived(
    (app.mode === 'studio') ||
    (app.mode === 'edit' && app.showVasePanel && (app.editTool === 'pen' || app.editTool === 'crossSection' || app.editTool === 'pattern'))
  );

  /** Which Pen sub-mode the canvas is editing right now. */
  let penPaneMode = $derived(
    app.editTool === 'crossSection' ? 'crossSection' :
    app.editTool === 'pattern' ? 'pattern' : 'profile'
  );

  $effect(() => {
    if (app.showVasePanel) app.rightPanel = 'vase';
  });

  async function applyPenPattern() {
    if (!app.penPattern || app.penPattern.anchors.length < 3) return;
    const svg = normalizeAndSerialize(app.penPattern);
    if (!svg) return;
    const cur = app.vaseParams.texture;
    const intensity = cur ? cur.intensity : 2;
    const repeatU = cur ? cur.repeatU : 4;
    const repeatV = cur ? cur.repeatV : 4;
    const grid = await svgToTextureGrid(svg, intensity, repeatU, repeatV, app.textureResolution);
    app.vaseParams = { ...app.vaseParams, texture: grid };
  }

  /** "Sıfırdan" — wipe EVERYTHING. Vase params reset to factory defaults
   *  (heights/radii/twist/noise) so the slider sees a fresh shape — not just
   *  cleared overrides. Triangles, slice cache, pen drafts, pattern, texture
   *  all go. Camera resets to iso. */
  function startFreshStudio() {
    app.triangles = [];
    app.fileName = '';
    app.sliceResult = null;
    app.gcodeOutput = null;
    app.vaseParams = {
      ...DEFAULT_VASE,
      texture: null,
      penProfile: null,
      penCrossSection: null,
      profilePoints: undefined
    };
    app.penPattern = null;
    app.editTool = 'pen';
    app.cameraView = 'iso';
    app.displayMode = 'default';
    app.viewMode = 'mesh';
    app.vaseEditingProfile = false;
    app.vaseEditUndoStack = [];
    app.penEditUndoStack = [];
    studioPrompt = false;
    enterMode('studio');
  }

  function continueInStudio() {
    studioPrompt = false;
    enterMode('studio');
  }

  const rightTabs: { id: RightPanel; label: string; icon: string; show: boolean }[] = $derived([
    { id: 'model',     label: 'Model',   icon: 'box',     show: true },
    { id: 'vase',      label: 'Vazo',    icon: 'aperture', show: app.showVasePanel },
    { id: 'sculpt',    label: 'Şekil',   icon: 'brush',   show: app.mode === 'sculpt' },
    { id: 'templates', label: 'Şablon',  icon: 'layers',  show: true },
    { id: 'gcode',     label: 'G-code',  icon: 'menu',    show: true }
  ]);
</script>

<div class="app" class:drag={isDragging}
  ondragover={onDragOver} ondragleave={onDragLeave} ondrop={onDrop}
  role="application">

  <Header onSetMode={setMode} onSaveProject={saveProject} onLoadProject={loadProjectClick} onExportMesh={exportMesh} />
  <input type="file" accept=".json,.clayforge.json" bind:this={projectInput}
    onchange={onProjectFile} style="display:none" />

  <div class="container">
    <ErrorBoundary>
      <LeftSidebar onOpenInsert={() => (showInsert = true)} />
    </ErrorBoundary>

    <main>
      <div class="toolbar">
        <div class="tool-icon-wrap">
          <Icon name="aperture" size={16} />
        </div>
        <span class="muted">Inspector</span>
        <div class="generate-slot">
          <MagicButton
            variant="primary"
            size="md"
            onclick={doSlice}
            disabled={(app.triangles.length === 0 && !app.showVasePanel) || app.status === 'slicing'}
            loading={app.status === 'slicing'}
            title="G-code üret + dilimle"
          >
            {#if app.status === 'slicing'}
              <Icon name="loader" size={15} class="spin-icon" />
              <span>{t(app.lang, 'state.slicing')}</span>
            {:else}
              <Icon name="zap" size={15} />
              <span>Generate G-code</span>
            {/if}
          </MagicButton>
        </div>
      </div>

      <ErrorBoundary>
        <div class="viewer-grid" class:pen-active={penPaneOpen} class:param-active={app.mode === 'param'} class:chat-active={chatOpen}>
        <div class="viewer">
          {#if app.mode === 'param'}
            <ParamEditor />
          {:else if ViewerComp}
            <ViewerComp />
          {:else}
            <div class="viewer-loading"><Icon name="loader" size={18} class="spin-icon" /> 3D motor yükleniyor…</div>
          {/if}
          <button class="ai-fab" onclick={toggleChat}
            class:active={chatOpen} title="AI Asistan ile konuşarak modelle">
            <Icon name="sparkles" size={14} />
            <span>AI</span>
          </button>
          {#if app.sliceResult && app.viewMode === 'layers'}
            <div class="layer-slider-overlay">
              <input type="range" min="0" max={app.sliceResult.layers.length - 1}
                value={app.currentLayerIndex}
                oninput={(e) => (app.currentLayerIndex = parseInt((e.currentTarget as HTMLInputElement).value))} />
              <span>{app.currentLayerIndex + 1} / {app.sliceResult.layers.length} · z={app.sliceResult.layers[app.currentLayerIndex].z.toFixed(1)}mm</span>
            </div>
          {/if}
          <div class="cam-overlay">
            {#each cameraOptions as v (v.id)}
              <button class="chip" class:active={app.cameraView === v.id} onclick={() => (app.cameraView = v.id)}>{v.label}</button>
            {/each}
          </div>
          <div class="display-overlay">
            {#each displayOptions as d (d.id)}
              <button class="chip" class:active={app.displayMode === d.id}
                onclick={() => { app.displayMode = d.id; persistSettings(); }}
                title={d.title}>{d.label}</button>
            {/each}
            <span class="overlay-divider"></span>
            <IconButton
              active={app.lightOn}
              onclick={() => { app.lightOn = !app.lightOn; persistSettings(); }}
              title={app.lightOn ? 'Işığı kapat (gölge yok)' : 'Işığı aç'}
              size={28}
            >
              <Icon name={app.lightOn ? 'sun' : 'moon'} size={14} />
            </IconButton>
            <IconButton
              active={app.edgeOn}
              onclick={() => { app.edgeOn = !app.edgeOn; persistSettings(); }}
              title={app.edgeOn ? 'Kenar çizgilerini kapat' : 'Kenar çizgilerini aç'}
              size={28}
            >
              <Icon name="grid" size={14} />
            </IconButton>
            {#if app.showVasePanel && app.mode !== 'studio'}
              <span class="overlay-divider"></span>
              <button class="tool-chip" class:active={app.editTool === 'pen'}
                onclick={() => { app.editTool = app.editTool === 'pen' ? 'handles' : 'pen'; persistSettings(); }}
                title="Pen ile sıfırdan profil çiz">
                <Icon name="edit-3" size={13} /> Pen
              </button>
              <button class="tool-chip" class:active={app.editTool === 'crossSection'}
                onclick={() => { app.editTool = app.editTool === 'crossSection' ? 'handles' : 'crossSection'; persistSettings(); }}
                title="Üstten görünüm — kesit şekli">
                <Icon name="crop-square" size={13} /> Kesit
              </button>
              <button class="tool-chip" class:active={app.editTool === 'pattern'}
                onclick={() => { app.editTool = app.editTool === 'pattern' ? 'handles' : 'pattern'; persistSettings(); }}
                title="Vektör pattern">
                <Icon name="brush" size={13} /> Pattern
              </button>
              {#if app.editTool === 'pattern' && app.penPattern}
                <button class="tool-chip primary" onclick={applyPenPattern}
                  title="Çizdiğin pattern'i vazoya texture olarak uygula">
                  <Icon name="sparkles" size={13} /> Uygula
                </button>
              {/if}
              <span class="overlay-divider"></span>
              <label class="chip color-chip" title="Gövde Rengi">
                <span class="swatch" style="background: {'#' + (app.vaseParams.baseColor ?? 0xc87b3a).toString(16).padStart(6, '0')}"></span>
                <input type="color"
                  value={'#' + (app.vaseParams.baseColor ?? 0xc87b3a).toString(16).padStart(6, '0')}
                  oninput={(e) => { app.vaseParams = { ...app.vaseParams, baseColor: parseInt((e.currentTarget as HTMLInputElement).value.slice(1), 16) }; }} />
              </label>
              <label class="chip color-chip" title="Aksen Rengi (Texture)">
                <span class="swatch" style="background: {'#' + (app.vaseParams.accentColor ?? 0xffffff).toString(16).padStart(6, '0')}"></span>
                <input type="color"
                  value={'#' + (app.vaseParams.accentColor ?? 0xffffff).toString(16).padStart(6, '0')}
                  oninput={(e) => { app.vaseParams = { ...app.vaseParams, accentColor: parseInt((e.currentTarget as HTMLInputElement).value.slice(1), 16) }; }} />
              </label>
            {/if}
          </div>
          {#if app.mode === 'edit' && (app.triangles.length > 0 || app.showVasePanel) && app.editTool !== 'pen' && app.editTool !== 'crossSection' && app.editTool !== 'pattern'}
            <div class="profile-edit-overlay">
              {#if !app.vaseEditingProfile}
                <button class="pill primary" onclick={startVaseEdit}>
                  <Icon name="sliders-horizontal" size={13} /> Profil Düzenle
                </button>
              {:else}
                <span class="hint">Sol-tık: nokta sürükle / yeni ekle • Sağ-tık: sil</span>
                <label class="sens" title="Drag hassasiyeti — düşük = daha hassas">
                  Hassasiyet
                  <input type="range" min="0.05" max="1" step="0.05"
                    value={app.vaseEditSensitivity}
                    oninput={(e) => { app.vaseEditSensitivity = parseFloat((e.currentTarget as HTMLInputElement).value); persistSettings(); }} />
                  <span class="sens-val">{app.vaseEditSensitivity.toFixed(2)}</span>
                </label>
                <button class="pill ghost" onclick={vaseEditUndo}
                  disabled={app.vaseEditUndoStack.length === 0}
                  title="Son değişikliği geri al (Ctrl+Z)">
                  <Icon name="undo" size={13} />
                </button>
                <button class="pill danger" onclick={cancelVaseEdit}>
                  <Icon name="x" size={13} /> İptal
                </button>
                <button class="pill ok" onclick={commitVaseEdit}>
                  <Icon name="check" size={13} /> Onayla
                </button>
              {/if}
            </div>
          {/if}
        </div>
        {#if penPaneOpen}
          <div class="pen-pane">
            {#if app.mode === 'studio'}
              <div class="studio-target-bar">
                <span class="muted">Yarat:</span>
                <button class="chip small" class:active={app.studioTarget === 'vase'}
                  onclick={() => { app.studioTarget = 'vase'; app.showVasePanel = true; app.rightPanel = 'vase'; persistSettings(); }}>
                  Vazo
                </button>
                <button class="chip small" class:active={app.studioTarget === 'mesh'}
                  onclick={() => { app.studioTarget = 'mesh'; app.rightPanel = 'model'; persistSettings(); }}>
                  Mesh
                </button>
                {#if app.studioTarget === 'vase'}
                  <span class="divider"></span>
                  <button class="chip small" class:active={app.editTool === 'pen'}
                    onclick={() => { app.editTool = 'pen'; persistSettings(); }}>Profil</button>
                  <button class="chip small" class:active={app.editTool === 'crossSection'}
                    onclick={() => { app.editTool = 'crossSection'; persistSettings(); }}>Kesit</button>
                  <button class="chip small" class:active={app.editTool === 'pattern'}
                    onclick={() => { app.editTool = 'pattern'; persistSettings(); }}>Pattern</button>
                  {#if app.editTool === 'pattern' && app.penPattern}
                    <button class="chip small primary" onclick={applyPenPattern}>Uygula</button>
                  {/if}
                {:else}
                  <span class="divider"></span>
                  <span class="muted">Mesh için pen sketch (yakında)</span>
                {/if}
              </div>
            {/if}
            {#key penPaneMode}
              <PenCanvas mode={penPaneMode as 'profile' | 'crossSection' | 'pattern'} />
            {/key}
          </div>
        {/if}
        {#if chatOpen}
          <div class="chat-pane">
            <ChatPanel onClose={() => setChatOpen(false)} />
          </div>
        {/if}
        </div>
      </ErrorBoundary>

      <div class="footer-strip">
        {#if app.gcodeOutput}
          <div class="param-row">
            <Icon name="layers" size={13} />
            <span class="label">Katman</span>
            <span class="value">{app.gcodeOutput.stats.layerCount}</span>
          </div>
          <div class="param-row">
            <Icon name="loader" size={13} />
            <span class="label">Süre</span>
            <span class="value">{app.gcodeOutput.stats.estimatedTimeMin.toFixed(1)}<small>m</small></span>
          </div>
          <div class="param-row">
            <Icon name="droplets" size={13} />
            <span class="label">Kil</span>
            <span class="value">{(app.gcodeOutput.stats.estimatedClayMm3 / 1000).toFixed(1)}<small>ml</small></span>
          </div>
        {:else}
          <span class="muted">Henüz dilimleme yok</span>
        {/if}
        <span class="status" class:ready={app.status === 'ready'} class:slicing={app.status === 'slicing'}>
          <span class="status-dot"></span>{app.status === 'ready' ? 'Idle' : app.status}
        </span>
      </div>
    </main>

    <aside class="right">
      <div class="right-tabs">
        {#each rightTabs as tab (tab.id)}
          {#if tab.show}
            <button class:active={app.rightPanel === tab.id}
              onclick={() => (app.rightPanel = tab.id)}>
              <Icon name={tab.icon} size={12} />
              <span>{tab.label}</span>
            </button>
          {/if}
        {/each}
      </div>

      <ErrorBoundary>
        <div class="right-content">
          {#if app.rightPanel === 'model'}
            <section class="block">
              <div class="section-title">Active Object</div>
              {#if app.triangles.length > 0}
                <div class="param-row">
                  <span class="label">Üçgen</span>
                  <span class="value">{app.triangles.length.toLocaleString()}</span>
                </div>
                <div class="param-row">
                  <span class="label">Dosya</span>
                  <span class="value small">{app.fileName}</span>
                </div>
                <div class="param-row">
                  <span class="label">Görünüm</span>
                  <span class="value">{app.viewMode === 'mesh' ? 'Mesh' : 'Slices'}</span>
                </div>
              {:else}
                <p class="muted">Sol panelden Mesh veya Vazo ekleyin.</p>
              {/if}
            </section>
          {:else if app.rightPanel === 'vase' && app.showVasePanel}
            <VasePanel />
          {:else if app.rightPanel === 'sculpt'}
            <SculptPanel />
          {:else if app.rightPanel === 'templates'}
            <TemplatesPanel />
          {:else if app.rightPanel === 'gcode'}
            <div class="gcode-pane">
              <div class="section-title row">
                <span>G-code / Terminal</span>
                {#if app.gcodeOutput}
                  <span class="row-actions">
                    <button class="chip small" onclick={copyGcode}>copy</button>
                    <button class="chip small primary" onclick={downloadGcode}>download</button>
                  </span>
                {/if}
              </div>
              {#if app.gcodeOutput}
                <pre>{gcodePreview()}</pre>
              {:else}
                <p class="muted">G-code yok. ⚡ ile dilimle.</p>
              {/if}
            </div>
          {/if}
        </div>
      </ErrorBoundary>
    </aside>
  </div>

  {#if isDragging}<div class="drop-overlay">{t(app.lang, 'btn.dropHint')}</div>{/if}
  {#if showInsert}<InsertPanel onClose={() => (showInsert = false)} />{/if}
  {#if app.showSvgGenerator}<SvgGeneratorModal onClose={() => (app.showSvgGenerator = false)} />{/if}
  {#if studioPrompt}
    <div class="studio-prompt-backdrop" onclick={() => (studioPrompt = false)} role="presentation">
      <div class="studio-prompt" onclick={(e) => e.stopPropagation()} role="dialog" aria-labelledby="studio-prompt-title">
        <h2 id="studio-prompt-title">Stüdyo'ya geçiş</h2>
        <p>Mevcut modelle mi devam etmek istersin, yoksa sıfırdan mı başlayalım?</p>
        <div class="studio-prompt-actions">
          <button class="pill ghost" onclick={() => (studioPrompt = false)}>İptal</button>
          <button class="pill" onclick={continueInStudio}>Mevcut modelle devam</button>
          <button class="pill primary" onclick={startFreshStudio}>Sıfırdan başla</button>
        </div>
      </div>
    </div>
  {/if}
</div>

<style>
  /* ===== Design tokens — high-contrast clay+cyan palette =====
     Brand: clay vase + GPU shaders. Primary = electric cyan (build/digital
     surface), secondary = clay amber (material identity). Background goes
     deep near-black blue for max contrast on mid-tone slate panels. */
  :global(:root) {
    --accent: #2e96ff;
    --accent-hover: #4ab2ff;
    --accent-deep: #0f5994;
    --accent-soft: rgba(46, 150, 255, 0.18);
    --accent-glow: rgba(74, 184, 255, 0.55);
    --clay: #f59e4b;
    --clay-deep: #d6803a;
    --clay-soft: rgba(245, 158, 75, 0.18);
    --danger: #f04a3a;
    --danger-soft: rgba(240, 74, 58, 0.16);
    --ok: #34d399;
    --ok-soft: rgba(52, 211, 153, 0.16);
    --warn: #fbbf24;
    --text-bright: #f0f7ff;
    --text-1: #e5eef9;
    --text-2: #b8d0e8;
    --text-3: #8aa6c2;
    --text-muted: #6e8fb0;
    --text-dim: #5d7995;
    --radius-card: 12px;
    --radius-chip: 8px;
    --radius-pill: 999px;
  }
  :global(:root[data-theme='dark']) {
    color-scheme: dark;
    --bg-0: #08111d;
    --bg-header-from: #0a1626;
    --bg-header-to: #0d1d33;
    --bg-viewer-from: #050d18;
    --bg-viewer-to: #0a1c2e;
    --panel-overlay: rgba(255, 255, 255, 0.025);
    --panel-overlay-strong: rgba(255, 255, 255, 0.05);
    --border-soft: rgba(255, 255, 255, 0.06);
    --border-dashed: rgba(255, 255, 255, 0.09);
    --chip-bg: rgba(255, 255, 255, 0.04);
    --chip-bg-hover: rgba(255, 255, 255, 0.09);
    --bg-2: #111a26;
    --bg-3: #16202e;
    --bg-3: #1c1c25;
    --fg-1: #e6eef6;
    --fg-2: #9eb8d6;
    --border: rgba(255, 255, 255, 0.05);
  }
  :global(:root[data-theme='light']) {
    color-scheme: light;
    --bg-0: #eef3f8;
    --bg-header-from: #ffffff;
    --bg-header-to: #f4f8fc;
    --bg-viewer-from: #e8f0f7;
    --bg-viewer-to: #d8e6f2;
    --panel-overlay: rgba(0, 0, 0, 0.02);
    --panel-overlay-strong: rgba(0, 0, 0, 0.04);
    --border-soft: rgba(0, 0, 0, 0.04);
    --border-dashed: rgba(0, 0, 0, 0.08);
    --chip-bg: rgba(0, 0, 0, 0.03);
    --chip-bg-hover: rgba(0, 0, 0, 0.07);
    --bg-2: #f4f8fc;
    --bg-3: #e8eef5;
    --fg-1: #15212d;
    --fg-2: #5e7691;
    --border: rgba(0, 0, 0, 0.06);
    --text-bright: #15212d;
    --text-1: #15212d;
    --text-2: #1d3a5a;
    --text-3: #4a6783;
    --text-muted: #5e7691;
    --text-dim: #6e89a3;
  }
  :global(html), :global(body), :global(#app) {
    height: 100%;
    margin: 0;
    padding: 0;
    overflow: hidden;
  }
  :global(body) {
    background:
      radial-gradient(80% 60% at 0% 0%, rgba(46,150,255,0.05), transparent 60%),
      radial-gradient(60% 50% at 100% 100%, rgba(245,158,75,0.04), transparent 60%),
      var(--bg-0);
    background-attachment: fixed;
    color: var(--text-1);
    font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;
    font-size: 13px; -webkit-font-smoothing: antialiased;
    font-feature-settings: 'cv11', 'ss01', 'tnum';
  }
  :global(.ms) {
    font-family: 'Material Symbols Outlined';
    font-weight: normal; font-style: normal;
    line-height: 1; vertical-align: middle;
    -webkit-font-feature-settings: 'liga'; -webkit-font-smoothing: antialiased;
    font-size: 18px;
  }
  :global(.ms.small) { font-size: 14px; }
  :global(.ms.spin) { animation: ms-spin 1s linear infinite; }
  @keyframes ms-spin { to { transform: rotate(360deg); } }
  :global(select option) { background: var(--bg-viewer-from); color: var(--text-1); }
  :global(select optgroup) { background: var(--bg-viewer-to); color: var(--text-3); font-weight: 600; font-style: normal; }
  :global(button:focus-visible),
  :global(a:focus-visible),
  :global(input:focus-visible),
  :global(select:focus-visible),
  :global(textarea:focus-visible),
  :global([role="dialog"]:focus-visible) {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }
  :global(:focus:not(:focus-visible)) { outline: none; }

  .app { display: flex; flex-direction: column; height: 100vh; background: var(--bg-0); overflow: hidden; }

  .container {
    display: grid;
    grid-template-columns: 280px 1fr 320px;
    grid-template-rows: 1fr;
    gap: 14px; padding: 14px;
    flex: 1; min-height: 0; overflow: hidden;
  }
  @media (max-width: 1100px) {
    .container { grid-template-columns: 240px 1fr 280px; gap: 10px; padding: 10px; }
  }
  @media (max-width: 820px) {
    .container {
      grid-template-columns: 1fr;
      grid-template-rows: auto 1fr auto;
      overflow-y: auto;
    }
    main { min-height: 50vh; }
  }
  main {
    background: linear-gradient(180deg, var(--panel-overlay), transparent);
    border: 1px solid var(--border-soft);
    border-radius: var(--radius-card);
    padding: 12px;
    overflow: hidden;
    min-height: 0;
    display: flex; flex-direction: column; gap: 12px;
  }

  .chip {
    background: var(--chip-bg); color: var(--text-2);
    padding: 6px 12px; border: none; border-radius: var(--radius-chip);
    font-size: 12px; font-weight: 500; cursor: pointer;
    font-family: inherit;
    transition: background 0.12s, color 0.12s;
  }
  .chip:hover { background: var(--chip-bg-hover); color: var(--text-bright); }
  .chip.active {
    background: var(--accent); color: white;
    box-shadow: 0 2px 6px rgba(26, 122, 201, 0.3);
  }
  .chip.small { padding: 4px 10px; font-size: 11px; }
  .chip.primary { background: var(--accent); color: white; }
  .chip.primary:hover { background: var(--accent-hover); }

  .section-title {
    font-size: 10px; color: var(--text-3);
    text-transform: uppercase; letter-spacing: 0.1em;
    font-weight: 600;
    margin-bottom: 8px;
    display: inline-flex; align-items: center; gap: 6px;
  }
  .section-title.row { display: flex; align-items: center; justify-content: space-between; }
  .row-actions { display: flex; gap: 4px; text-transform: none; letter-spacing: 0; }
  .block { display: flex; flex-direction: column; }

  .toolbar { display: flex; align-items: center; gap: 12px; padding: 4px 0; }
  .tool-icon-wrap {
    width: 32px; height: 32px;
    background: linear-gradient(135deg, var(--chip-bg), rgba(255,255,255,0.01));
    border: 1px solid var(--border-soft);
    border-radius: 9px;
    display: inline-flex; align-items: center; justify-content: center;
    color: var(--text-2);
    box-shadow: 0 1px 0 rgba(255,255,255,0.04) inset;
  }
  .muted { color: var(--text-muted); font-size: 13px; font-weight: 500; }
  .generate-slot { margin-left: auto; }
  :global(.spin-icon) { animation: spin-icon 1s linear infinite; }
  @keyframes spin-icon { to { transform: rotate(360deg); } }

  .btn-generate {
    margin-left: auto;
    background: var(--accent); color: white; border: none;
    padding: 8px 16px; border-radius: var(--radius-chip);
    font-size: 13px; font-weight: 600; cursor: pointer; font-family: inherit;
    display: inline-flex; align-items: center; gap: 6px;
    box-shadow: 0 2px 10px rgba(26, 122, 201, 0.3);
    transition: background 0.12s;
  }
  .btn-generate:hover:not(:disabled) { background: var(--accent-hover); }
  .btn-generate:disabled { background: var(--chip-bg); color: var(--text-muted); cursor: not-allowed; box-shadow: none; }
  .btn-generate .ms { font-size: 16px; }

  .viewer {
    flex: 1;
    display: flex;
    flex-direction: column;
    background: linear-gradient(180deg, var(--bg-viewer-from) 0%, var(--bg-viewer-to) 100%);
    border-radius: 8px; min-height: 360px;
    border: 1px dashed var(--border-dashed);
    overflow: hidden; min-height: 0; position: relative;
  }
  .viewer-grid {
    flex: 1; min-height: 0; min-width: 0;
    display: flex; gap: 10px;
  }
  .viewer-grid > .viewer { min-width: 0; flex: 1; }
  .viewer-grid.pen-active { display: grid; grid-template-columns: minmax(0, 6fr) minmax(0, 4fr); }
  .viewer-grid.chat-active { display: grid; grid-template-columns: minmax(0, 1fr) 360px; }
  .pen-pane { min-height: 0; min-width: 0; display: flex; flex-direction: column; overflow: hidden; gap: 6px; }
  .pen-pane :global(.pen-canvas) { flex: 1; min-width: 0; }
  .chat-pane {
    min-height: 0; min-width: 0;
    display: flex;
    overflow: hidden;
  }
  .chat-pane :global(.chat-panel) { width: 100% !important; }
  .ai-fab {
    position: absolute; bottom: 14px; right: 14px;
    display: inline-flex; align-items: center; gap: 6px;
    padding: 9px 14px; border-radius: 999px;
    background: linear-gradient(135deg, #2e96ff, #c084fc);
    color: white; border: 1px solid rgba(255,255,255,0.18);
    font-size: 12px; font-weight: 700; letter-spacing: 0.02em;
    cursor: pointer; font-family: inherit;
    box-shadow:
      0 0 0 1px rgba(255,255,255,0.08) inset,
      0 12px 28px -8px rgba(192,132,252,0.55);
    z-index: 30;
    transition: transform 0.15s, box-shadow 0.18s;
  }
  .ai-fab:hover { transform: translateY(-2px); box-shadow: 0 16px 36px -8px rgba(192,132,252,0.7); }
  .ai-fab.active {
    background: linear-gradient(135deg, #c084fc, #f59e4b);
    box-shadow: 0 16px 36px -8px rgba(245,158,75,0.6);
  }
  .studio-target-bar {
    display: flex; align-items: center; gap: 6px; flex-wrap: wrap;
    padding: 6px 8px;
    background: var(--panel-overlay);
    border: 1px solid var(--border-soft);
    border-radius: 8px;
  }
  .studio-target-bar .muted { color: var(--text-muted); font-size: 11px; }
  .studio-target-bar .divider { width: 1px; height: 18px; background: var(--border-dashed); margin: 0 4px; }
  @media (max-width: 1100px) {
    .viewer-grid.pen-active {
      grid-template-columns: minmax(0, 1fr);
      grid-template-rows: minmax(0, 1fr) minmax(0, 1fr);
    }
  }
  .viewer-loading {
    position: absolute; inset: 0; display: grid; place-items: center;
    color: var(--text-muted); font-size: 12px; gap: 6px; flex-direction: column;
  }
  .layer-slider-overlay {
    position: absolute; bottom: 12px; left: 12px; right: 12px;
    display: flex; align-items: center; gap: 10px;
    background: rgba(7, 17, 26, 0.85); padding: 6px 12px; border-radius: 8px;
    backdrop-filter: blur(10px);
    color: var(--text-2); font-size: 11px;
    border: 1px solid var(--border-soft);
  }
  .layer-slider-overlay input { flex: 1; accent-color: var(--accent); }
  .cam-overlay {
    position: absolute; top: 12px; right: 12px;
    display: flex; gap: 2px;
    background: rgba(7, 17, 26, 0.78); padding: 4px; border-radius: 10px;
    backdrop-filter: blur(12px) saturate(140%);
    border: 1px solid var(--border-soft);
    box-shadow: 0 8px 28px -10px rgba(0,0,0,0.55);
  }
  .cam-overlay .chip { background: transparent; padding: 5px 11px; font-size: 11px; border-radius: 6px; }
  .cam-overlay .chip:hover { background: rgba(255,255,255,0.06); }
  .cam-overlay .chip.active {
    background: linear-gradient(180deg, rgba(26,122,201,0.32), rgba(26,122,201,0.18));
    color: #d6ecff;
    box-shadow: 0 0 0 1px rgba(74,184,255,0.3) inset;
  }
  .display-overlay {
    position: absolute; top: 56px; right: 12px;
    display: flex; gap: 4px; align-items: center;
    background: rgba(7, 17, 26, 0.78); padding: 5px; border-radius: 10px;
    backdrop-filter: blur(12px) saturate(140%);
    border: 1px solid var(--border-soft);
    box-shadow: 0 8px 28px -10px rgba(0,0,0,0.55);
  }
  .display-overlay .chip { background: transparent; padding: 4px 10px; font-size: 11px; }
  .display-overlay .chip.active { background: var(--accent); color: white; }
  .overlay-divider { width: 1px; height: 18px; background: rgba(255,255,255,0.08); margin: 0 2px; }
  .tool-chip {
    display: inline-flex; align-items: center; gap: 5px;
    background: transparent;
    color: var(--text-2);
    padding: 5px 10px;
    border: 1px solid transparent;
    border-radius: 7px;
    font-size: 11px; font-weight: 500;
    cursor: pointer; font-family: inherit;
    transition: background 0.15s, color 0.15s, border-color 0.15s, transform 0.1s;
  }
  .tool-chip:hover { background: rgba(255,255,255,0.06); color: var(--text-bright); }
  .tool-chip:active { transform: scale(0.96); }
  .tool-chip.active {
    background: linear-gradient(180deg, rgba(26,122,201,0.32), rgba(26,122,201,0.18));
    color: #d6ecff;
    border-color: rgba(74,184,255,0.4);
    box-shadow: 0 0 0 1px rgba(74,184,255,0.18) inset, 0 4px 12px -4px rgba(74,184,255,0.45);
  }
  .tool-chip.primary {
    background: linear-gradient(180deg, #1a7ac9, #0f5994);
    color: white; border-color: rgba(255,255,255,0.12);
    box-shadow: 0 4px 14px -4px rgba(74,184,255,0.6);
  }
  .display-overlay .color-chip { padding: 2px 6px; display: inline-flex; align-items: center; gap: 0; cursor: pointer; }
  .display-overlay .color-chip .swatch { width: 18px; height: 18px; border-radius: 4px; border: 1px solid rgba(255,255,255,0.25); display: inline-block; box-shadow: 0 1px 0 rgba(0,0,0,0.3) inset; }
  .display-overlay .color-chip input[type=color] { width: 0; height: 0; padding: 0; border: 0; opacity: 0; position: absolute; }
  .profile-edit-overlay {
    position: absolute; top: 12px; left: 12px;
    display: flex; gap: 6px; align-items: center;
    background: rgba(7, 17, 26, 0.78); padding: 6px; border-radius: var(--radius-chip);
    backdrop-filter: blur(10px); border: 1px solid var(--border-soft);
    z-index: 5;
  }
  .profile-edit-overlay .hint {
    color: var(--text-muted); font-size: 11px; padding: 0 6px;
  }
  .profile-edit-overlay .pill {
    display: inline-flex; align-items: center; gap: 4px;
    padding: 6px 12px; border-radius: var(--radius-pill);
    font-size: 11.5px; font-weight: 600; cursor: pointer; font-family: inherit;
    border: 1px solid transparent;
  }
  .profile-edit-overlay .pill.primary { background: var(--accent); color: white; }
  .profile-edit-overlay .pill.primary:hover { background: var(--accent-hover); }
  .profile-edit-overlay .pill.ok { background: rgba(43, 182, 115, 0.85); color: white; }
  .profile-edit-overlay .pill.ok:hover { background: rgba(43, 182, 115, 1); }
  .profile-edit-overlay .pill.danger { background: rgba(208, 74, 58, 0.18); color: #ffaaa0; border-color: rgba(208,74,58,0.4); }
  .profile-edit-overlay .pill.danger:hover { background: rgba(208, 74, 58, 0.32); }
  .profile-edit-overlay .pill.ghost { background: var(--chip-bg); color: var(--text-2); }
  .profile-edit-overlay .pill.ghost:hover { background: var(--chip-bg-hover); color: var(--text-bright); }
  .profile-edit-overlay .pill:disabled { opacity: 0.4; cursor: not-allowed; }
  .profile-edit-overlay .sens {
    display: inline-flex; align-items: center; gap: 6px;
    color: var(--text-2); font-size: 11px; padding: 0 6px;
  }
  .profile-edit-overlay .sens input[type="range"] { width: 80px; accent-color: var(--accent); }
  .profile-edit-overlay .sens-val { font-variant-numeric: tabular-nums; min-width: 28px; color: var(--text-bright); }

  .footer-strip {
    display: flex; align-items: center; gap: 18px;
    padding: 9px 14px;
    background: linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0.01));
    border: 1px solid var(--border-soft);
    border-radius: 10px;
    flex-shrink: 0;
  }
  .footer-strip .param-row { display: flex; align-items: center; gap: 6px; border: none; padding: 0; }
  .footer-strip .label { color: var(--text-3); font-size: 11px; text-transform: uppercase; letter-spacing: 0.06em; font-weight: 600; }
  .footer-strip .value small { font-size: 10px; color: var(--text-muted); margin-left: 1px; font-weight: 600; }
  .footer-strip .value { color: var(--text-bright); font-weight: 700; font-size: 13px; font-variant-numeric: tabular-nums; }
  .footer-strip :global(.lucide) { color: var(--text-muted); }
  .status {
    margin-left: auto; display: inline-flex; align-items: center; gap: 6px;
    color: var(--text-3); font-size: 11px; text-transform: uppercase; letter-spacing: 0.06em; font-weight: 600;
    padding: 4px 10px;
    background: var(--chip-bg);
    border-radius: 999px;
    border: 1px solid var(--border-soft);
  }
  .status-dot {
    display: inline-block; width: 7px; height: 7px; border-radius: 50%;
    background: var(--text-muted);
    box-shadow: 0 0 0 2px rgba(255,255,255,0.04);
  }
  .status.ready .status-dot { background: var(--ok); box-shadow: 0 0 0 2px rgba(43,182,115,0.18), 0 0 8px rgba(43,182,115,0.6); }
  .status.slicing .status-dot { background: var(--accent); animation: pulse 1s infinite; box-shadow: 0 0 0 2px rgba(74,184,255,0.18), 0 0 8px rgba(74,184,255,0.6); }
  @keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.4; } }

  .right {
    display: flex; flex-direction: column; gap: 8px;
    background: linear-gradient(180deg, var(--panel-overlay), transparent);
    border: 1px solid var(--border-soft);
    border-radius: var(--radius-card);
    padding: 14px;
    overflow-y: auto;
  }
  .right-tabs {
    display: flex; gap: 3px; padding: 4px;
    background: rgba(255,255,255,0.025);
    border: 1px solid var(--border-soft);
    border-radius: 10px; margin-bottom: 14px;
  }
  .right-tabs button {
    flex: 1; padding: 7px 8px;
    background: transparent; border: 1px solid transparent;
    color: var(--text-3); font-size: 11px; font-weight: 600;
    border-radius: 7px; cursor: pointer; font-family: inherit;
    display: inline-flex; align-items: center; justify-content: center; gap: 5px;
    transition: background 0.14s, color 0.14s, border-color 0.14s, transform 0.1s;
  }
  .right-tabs button:hover:not(.active) { color: var(--text-bright); background: rgba(255,255,255,0.04); }
  .right-tabs button:active { transform: scale(0.97); }
  .right-tabs button.active {
    background: linear-gradient(180deg, rgba(26,122,201,0.32), rgba(26,122,201,0.16));
    color: #d6ecff;
    border-color: rgba(74,184,255,0.35);
    box-shadow: 0 0 0 1px rgba(74,184,255,0.12) inset, 0 4px 12px -4px rgba(74,184,255,0.4);
  }
  .right-content { flex: 1; overflow: hidden; min-height: 0; display: flex; flex-direction: column; }

  .param-row {
    display: flex; justify-content: space-between; align-items: center;
    gap: 12px; padding: 8px 0;
    border-bottom: 1px dashed var(--border-dashed);
  }
  .param-row:last-child { border-bottom: none; }
  .label { color: var(--text-2); font-size: 13px; }
  .value { color: var(--text-bright); font-weight: 600; font-size: 13px; }
  .value.small { font-size: 11px; opacity: 0.85; max-width: 160px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

  .gcode-pane { display: flex; flex-direction: column; height: 100%; gap: 8px; }
  .gcode-pane pre {
    margin: 0;
    background: var(--bg-viewer-from);
    border: 1px solid var(--border-soft);
    border-radius: 8px;
    padding: 10px; font-size: 10.5px; line-height: 1.45;
    flex: 1; overflow: auto; white-space: pre;
    color: var(--text-2);
    font-family: ui-monospace, 'SF Mono', 'JetBrains Mono', monospace;
  }

  .app.drag { outline: 3px dashed var(--accent); outline-offset: -8px; }
  .studio-prompt-backdrop {
    position: fixed; inset: 0; background: rgba(7, 17, 26, 0.65);
    backdrop-filter: blur(4px);
    display: grid; place-items: center; z-index: 9998;
  }
  .studio-prompt {
    background: var(--bg-2);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 22px 24px;
    max-width: 440px;
    box-shadow: 0 12px 32px rgba(0,0,0,0.45);
  }
  .studio-prompt h2 { margin: 0 0 8px; font-size: 17px; color: var(--text-bright); }
  .studio-prompt p { margin: 0 0 16px; color: var(--text-2); font-size: 13px; line-height: 1.5; }
  .studio-prompt-actions { display: flex; gap: 8px; flex-wrap: wrap; justify-content: flex-end; }
  .studio-prompt-actions .pill {
    display: inline-flex; align-items: center; gap: 4px;
    padding: 8px 14px; border-radius: var(--radius-pill);
    font-size: 12px; font-weight: 600; cursor: pointer; font-family: inherit;
    border: 1px solid transparent; background: var(--chip-bg); color: var(--text-2);
  }
  .studio-prompt-actions .pill:hover { background: var(--chip-bg-hover); color: var(--text-bright); }
  .studio-prompt-actions .pill.primary { background: var(--accent); color: white; }
  .studio-prompt-actions .pill.primary:hover { background: var(--accent-hover); }
  .studio-prompt-actions .pill.ghost { background: transparent; color: var(--text-muted); }
  .drop-overlay {
    position: fixed; inset: 12px; display: grid; place-items: center;
    background: var(--accent-soft); color: #bfe6ff;
    font-size: 22px; font-weight: 600; border-radius: 12px;
    pointer-events: none; z-index: 9999;
    border: 2px dashed var(--accent);
  }
</style>
